import type { ReactNode } from 'react';
import { Anchor, Button, Group, Image, Stack, Text, Title } from '@mantine/core';
import { Link } from 'react-router-dom';

interface ProgramHeaderProps {
  logoUrl?: string;
  eyebrow?: string;
  programName: string;
  portalName?: string;
  subtitle?: string;
  subtitleNote?: ReactNode;
  subtitlePadding?: number | string;
  titleOrder?: 1 | 2 | 3 | 4 | 5 | 6;
  titleAlign?: 'left' | 'center';
  containerSize?: string;
  mb?: number | string;
  showTopBar?: boolean;
  showTopBarLogo?: boolean;
  showTopBarPortalName?: boolean;
  topBarLogoUrl?: string;
  topBarLogoAlt?: string;
  topBarLogoWidth?: number;
  topBarLogoHeight?: number;
  hideTitle?: boolean;
  sessionEmail?: string;
  onLogout?: () => void;
  isLoggingOut?: boolean;
}

export default function ProgramHeader({
  logoUrl,
  eyebrow,
  programName,
  portalName = 'RIS Rx Portal',
  subtitle,
  subtitleNote,
  subtitlePadding = 'md',
  titleOrder = 2,
  titleAlign = 'center',
  containerSize: _containerSize = 'xl',
  mb = 'xl',
  showTopBar = false,
  showTopBarLogo = true,
  showTopBarPortalName = true,
  topBarLogoUrl = '/ris-rx-primary-DWxz-PLF.png',
  topBarLogoAlt = 'RIS Rx',
  topBarLogoWidth = 108,
  topBarLogoHeight = 32,
  hideTitle = false,
  sessionEmail,
  onLogout,
  isLoggingOut = false,
}: ProgramHeaderProps) {
  return (
    <div className="portal-header" style={{ marginBottom: mb }}>
      {showTopBar && (
        <div className="portal-topbar">
          <Group justify="space-between" align="center" className="portal-shell-inner" wrap="wrap">
            <Anchor component={Link} to="/" underline="never" c="inherit">
              <Group gap="sm" wrap="nowrap">
                {showTopBarLogo && (
                  <Image
                    src={topBarLogoUrl}
                    alt={topBarLogoAlt}
                    maw={topBarLogoWidth}
                    w={showTopBarPortalName ? undefined : topBarLogoWidth}
                    mah={topBarLogoHeight}
                    fit="contain"
                    className="portal-header__brand-logo"
                  />
                )}
                {showTopBarPortalName && portalName && (
                  <>
                    {showTopBarLogo && '|'}
                    <Stack gap={0}>
                      <Text className="portal-brand-label">{portalName}</Text>
                    </Stack>
                  </>
                )}
              </Group>
            </Anchor>
            <Group gap="xs" wrap="wrap">
              {sessionEmail ? (
                <>
                  <Text size="xs" className="portal-identity-pill">{sessionEmail}</Text>
                  {onLogout && (
                    <Button variant="subtle" color="gray" size="xs" onClick={onLogout} loading={isLoggingOut}>
                      Logout
                    </Button>
                  )}
                </>
              ) : null}
            </Group>
          </Group>
        </div>
      )}

      {!hideTitle && (
      <div className="portal-program-hero" style={!logoUrl ? { paddingTop: 28 } : undefined}>
        <div className="portal-program-hero__inner">
          <Stack align={titleAlign === 'left' ? 'flex-start' : 'center'} gap="xs">
            {logoUrl && (
              <Image
                src={logoUrl}
                alt={programName}
                maw={200}
                fit="contain"
                className="portal-header__logo"
              />
            )}
            {eyebrow && (
              <Text size="sm" fw={600} ta={titleAlign} className="portal-eyebrow">
                {eyebrow}
              </Text>
            )}
            <Title order={titleOrder} ta={titleAlign} className="portal-title">
              {programName}
            </Title>
            {subtitle && (
              <Text size="sm" ta={titleAlign} className="portal-subtitle" pb={subtitleNote ? 0 : subtitlePadding}>
                {subtitle}
              </Text>
            )}
            {subtitleNote && (
              <Text size="xs" ta={titleAlign} c="dimmed" pb={subtitlePadding}>
                {subtitleNote}
              </Text>
            )}
          </Stack>
        </div>
      </div>
      )}
    </div>
  );
}